import { InteractionStepTaskComponent } from '../../InteractionStep';
import { InteractionStepType } from '@scale/shared/types/chat';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import { useContext, useRef, useCallback, useMemo, memo } from 'react';
import { Button, Typography } from '@scale/scaleui';
import {
  useStepParams,
  useMostRecentResponse,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import {
  constructStepChildId,
  useWorkerCommentsStore,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { Alert } from '../../TempComponents';
import {
  EditorContainer,
  Preview,
  MarkdownPreviewContext,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor';
import WorkerCommentBridge from '../ModelResponseEditor/ModelResponseEditorTaskComponent/WorkerCommentBridge';
import { TextSelectionPopover } from '../ModelResponseEditor/ModelResponseEditorTaskComponent/TextSelectionPopover';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { IconButton, Icon } from '@scale/scaleui-radix';

const SpanFeedbackTaskComponent: InteractionStepTaskComponent = ({ readOnly, responseIndex }) => {
  const taskContext = useContext(ChatTaskContext);

  const step = useStepParams<InteractionStepType.ModelResponseEditor>(responseIndex);
  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });

  const mostRecentResponse = useMostRecentResponse(responseIndex);

  const text = mostRecentResponse;

  const baseResponseHash = useMemo(() => {
    if (!text) return null;

    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).substr(0, 8);
  }, [text]);

  const childId = constructStepChildId(step, baseResponseHash || undefined);
  const containerRef = useRef(null);
  const cardRef = useRef(null);

  const comments = useWorkerCommentsStore(state => state.workerComments);
  const commentsForThisStep = useMemo(
    () => comments.filter(comment => comment.childId === childId),
    [comments, childId],
  );

  const createAndSubmitResponse = useCallback(() => {
    const formattedResponse = `This was your original response:\n\n${mostRecentResponse}\n\nFeedback:\n${commentsForThisStep
      .map(comment => {
        if (!comment.locationStart || !comment.locationEnd) {
          return `- Comment: ${comment.comment}\n`;
        }
        const span = text.substring(comment.locationStart, comment.locationEnd);
        return `- Span: "${span}"\n  Comment: ${comment.comment}\n`;
      })
      .join('\n')}`;

    const responseToSubmit = {
      type: InteractionStepType.SpanFeedback,
      context: {
        comments: commentsForThisStep,
        baseResponse: mostRecentResponse,
        isComplete: false,
        multiturnContinueOutput: 'The model will now improve its response based on your feedback.',
        shouldContinue: true,
      },
      output: formattedResponse,
    };
    onSubmit(responseToSubmit, responseIndex, step, true);
  }, [mostRecentResponse, onSubmit, commentsForThisStep, responseIndex, step, text]);

  const removeComment = useCallback((workerCommentId: string) => {
    useWorkerCommentsStore.getState().deleteWorkerComment(workerCommentId);
  }, []);

  const error = useMemo(() => {
    if (commentsForThisStep.length === 0) {
      return 'Please provide feedback by highlighting a span of text and adding a comment';
    }
    return null;
  }, [commentsForThisStep]);

  return (
    <StepCard>
      <StepCard.Header>
        <StepCard.Title>Span Feedback</StepCard.Title>
      </StepCard.Header>
      <StepCard.Body>
        <div ref={cardRef} className={(readOnly ? 'cursor-not-allowed' : '') + ' max-w-[700px]'}>
          <MarkdownPreviewContext
            content={text}
            enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
          >
            <EditorContainer ref={containerRef} className="border-none">
              <Preview content={text} />
            </EditorContainer>

            <WorkerCommentBridge enabled childId={childId} />
            <TextSelectionPopover
              anchorRef={cardRef}
              enabled
              childId={childId}
              turnIndex={step.turn}
            />
          </MarkdownPreviewContext>
        </div>
      </StepCard.Body>
      <StepCard.Footer className="flex flex-col gap-2 w-full">
        <div className="flex flex-col gap-2 w-full">
          {commentsForThisStep.map((comment, index) => {
            if (!comment.locationStart || !comment.locationEnd) {
              return (
                <div key={index} className="flex items-center justify-between">
                  <Typography.Paragraph3>
                    <span className="font-semibold text-green-600">Comment:</span>
                    <span className="ml-2">{comment.comment}</span>
                  </Typography.Paragraph3>
                  <IconButton
                    variant="ghost"
                    onClick={() => removeComment(comment.workerCommentId)}
                  >
                    <Icon label="Search" icon={faTrash} size="1" />
                  </IconButton>
                </div>
              );
            }
            return (
              <div key={index} className="flex items-center justify-between">
                <Typography.Paragraph3>
                  <div>
                    <span className="font-semibold text-blue-600">Span:</span>
                    <code className="ml-2 p-1 bg-gray-200 rounded text-sm">
                      {text.slice(comment.locationStart - 1, comment.locationEnd - 1)}
                    </code>
                  </div>
                  <div>
                    <span className="font-semibold text-green-600">Comment:</span>
                    <span className="ml-2">{comment.comment}</span>
                  </div>
                </Typography.Paragraph3>
                <IconButton variant="ghost" onClick={() => removeComment(comment.workerCommentId)}>
                  <Icon label="Search" icon={faTrash} size="1" />
                </IconButton>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col gap-2 w-full">
          {error && (
            <Alert variant="warning" className="my-2">
              <Typography.Paragraph2>{error}</Typography.Paragraph2>
            </Alert>
          )}
          <div className="flex flex-row justify-end gap-2 w-full">
            <Button onClick={createAndSubmitResponse} disabled={!!error}>
              Submit {commentsForThisStep.length} comments
            </Button>
            <Button
              disabled={commentsForThisStep.length > 0}
              variant="danger"
              onClick={() => {
                if (confirm('Are you sure you want to end the chat with no comments?')) {
                  onSubmit(
                    {
                      type: InteractionStepType.SpanFeedback,
                      context: {
                        comments: [],
                        shouldContinue: false,
                        isComplete: true,
                      },
                      output: text,
                    },
                    responseIndex,
                    step,
                  );
                }
              }}
            >
              End Session
            </Button>
          </div>
        </div>
      </StepCard.Footer>
    </StepCard>
  );
};

export default memo(SpanFeedbackTaskComponent);
